<script setup lang="ts">
import {computed, onBeforeUnmount} from 'vue';
import {formatNodeInfo, getSelectorForEntityId, getNodeLayers, type TreeNodeWithPage} from './diff-grouping.ts';
import {FILE_DIFF_STATUS} from '../../types/file_tree.ts';
import {statusIcon, statusClasses} from '../FileTreeItems/utils.ts';
import {SvgIcon} from '../../../svg.ts';
import {type SchemaType, SCHEMA_TYPES} from '../../../features/allspicejson.ts';
import {openAndScrollToPage, queryPageScopedElements} from './page-navigation.ts';

import {useLayersStore} from '../../stores/layers.ts';
const layersStore = useLayersStore();

const props = withDefaults(defineProps<{
  item: TreeNodeWithPage;
  groupName: string;
  pageName?: string | undefined;
  schemaType: SchemaType;
  diffTree: TreeNodeWithPage;
  navigationRootId?: string;
  spacing?: 'loose' | 'tight';
}>(), {
  pageName: undefined,
  navigationRootId: undefined,
  spacing: 'loose',
});

function getNavigationRoot(): ParentNode {
  if (!props.navigationRootId) {
    return document;
  }

  return document.querySelector(`#${CSS.escape(props.navigationRootId)}`) ?? document;
}

const statusDisplay = {
  [FILE_DIFF_STATUS.ADDED]: {
    borderClass: 'tw:border-s-green',
  },
  [FILE_DIFF_STATUS.DELETED]: {
    borderClass: 'tw:border-s-red',
  },
  [FILE_DIFF_STATUS.MODIFIED]: {
    borderClass: 'tw:border-s-yellow',
  },
  [FILE_DIFF_STATUS.UNCHANGED]: {
    borderClass: 'tw:border-s-purple',
  },
};

const status = computed(() => {
  if (props.item.added) {
    return FILE_DIFF_STATUS.ADDED;
  } else if (props.item.removed) {
    return FILE_DIFF_STATUS.DELETED;
  } else if (props.item.modified) {
    return FILE_DIFF_STATUS.MODIFIED;
  }
  return FILE_DIFF_STATUS.UNCHANGED;
});

const info = computed(() => {
  return formatNodeInfo(props.schemaType, props.item, props.groupName);
});

const VISIBILITY = {
  NONE: 'none',
  PARTIAL: 'partial',
  TOTAL: 'total',
} as const;
const visibility = computed(() => {
  // Layer visibility only applies to PCB files — schematics and other file
  // types have no layer associations.  The layers store is global, so it may
  // still contain PCB layers after navigating away from a PcbDoc.
  if (!layersStore.hasLayers || props.schemaType !== SCHEMA_TYPES.PCB) {
    return VISIBILITY.TOTAL;
  }

  const itemLayerIds = getNodeLayers(props.schemaType, props.item, props.groupName, props.diffTree);
  const visibleItemLayerIds = itemLayerIds.filter((layerId) => layersStore.visibleLayerIds.includes(layerId));

  if (visibleItemLayerIds.length === 0) {
    return VISIBILITY.NONE;
  } else if (visibleItemLayerIds.length < itemLayerIds.length) {
    return VISIBILITY.PARTIAL;
  }
  return VISIBILITY.TOTAL;
});

async function handleClick() {
  if (visibility.value === VISIBILITY.NONE) {
    return;
  }

  await navigateToPage(props.item.page);

  // TODO: extract this to diff-grouping.ts
  const selector = props.groupName === 'component_instances' ? `[data-path^='component_instances/${props.item.key}']` : `[data-id='${props.item.key}']`;

  // get the biggest matching element to be the target
  const targetEl = findBiggestEl(queryPageScopedElements(getNavigationRoot(), props.item.page, selector));
  if (!targetEl) {
    console.debug(`No element found with data-id ${props.item.key}`);
    return;
  }

  const inspectorRequestEvent = new CustomEvent<{
    target: Element,
    selector: string;
    treeNode: TreeNodeWithPage;
  }>('inspector-request', {
    detail: {
      target: targetEl,
      selector,
      treeNode: props.item,
    },
    bubbles: true,
    cancelable: true,
    composed: true,
  });

  const svgEl = targetEl.closest('svg');
  svgEl.dispatchEvent(inspectorRequestEvent);
}

async function navigateToPage(pageId: string): Promise<void> {
  await openAndScrollToPage(getNavigationRoot(), pageId);
  handleHoverStart();
}

function findBiggestEl(els: Element[]) {
  if (els.length === 0) {
    return null;
  }

  let biggest = els[0];
  const biggestRect = biggest.getBoundingClientRect();
  let biggestArea = biggestRect.width * biggestRect.height;

  for (const el of els) {
    const elRect = el.getBoundingClientRect();
    const elArea = elRect.width * elRect.height;
    if (elArea > biggestArea) {
      biggest = el;
      biggestArea = elArea;
    }
  }

  return biggest;
}

let highlightRect: SVGRectElement = null;
function handleHoverStart() {
  handleHoverEnd();

  const selector = getSelectorForEntityId(props.schemaType, props.item.key, props.groupName);
  const selectedEls = queryPageScopedElements(getNavigationRoot(), props.item.page, selector);
  // bail if we didn't find any highlightable elements
  if (selectedEls.length === 0) {
    return;
  }

  // figure out a rectangle that encloses those groups
  const EXPAND_BY = 2;
  const dimensions = makeEnclosingRectangle(selectedEls as SVGGraphicsElement[], EXPAND_BY);
  if (!(
    Number.isFinite(dimensions.x) && Number.isFinite(dimensions.y) &&
    Number.isFinite(dimensions.width) && Number.isFinite(dimensions.height)
  )) {
    // there's nothing to highlight, probably because there aren't any layers visible
    return;
  }

  // create a <rect> to overlay on them
  const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
  rect.classList.add('highlight');

  rect.setAttribute('x', dimensions.x.toString());
  rect.setAttribute('y', dimensions.y.toString());
  rect.setAttribute('width', dimensions.width.toString());
  rect.setAttribute('height', dimensions.height.toString());

  rect.style.fill = 'var(--color-primary-alpha-30)';
  rect.style.strokeWidth = '1';
  rect.style.stroke = 'var(--color-primary)';

  // put it in the SVG
  const svgEl = selectedEls[0].closest('svg');
  svgEl.append(rect);

  highlightRect = rect;
}

function handleHoverEnd() {
  highlightRect?.remove();
  highlightRect = null;
}

onBeforeUnmount(() => {
  handleHoverEnd();
});

function makeEnclosingRectangle(selectedEls: SVGGraphicsElement[], expandBy = 0) {
  const svgEl = selectedEls[0].closest('svg') as SVGSVGElement;
  const svgScreenCTM = svgEl.getScreenCTM();
  if (!svgScreenCTM) return {x: NaN, y: NaN, width: NaN, height: NaN};
  const inverseCTM = svgScreenCTM.inverse();

  const bounds = {
    left: Infinity,
    right: -Infinity,
    top: Infinity,
    bottom: -Infinity,
  };

  for (const el of selectedEls) {
    const clientRect = el.getBoundingClientRect();
    if (clientRect.width === 0 || clientRect.height === 0) {
      continue;
    }

    // Convert screen coordinates to SVG coordinate space, which correctly
    // accounts for all ancestor transforms (viewBox, nested groups, etc.).
    const pt = svgEl.createSVGPoint();

    pt.x = clientRect.left;
    pt.y = clientRect.top;
    const topLeft = pt.matrixTransform(inverseCTM);

    pt.x = clientRect.right;
    pt.y = clientRect.bottom;
    const bottomRight = pt.matrixTransform(inverseCTM);

    bounds.left = Math.min(bounds.left, topLeft.x);
    bounds.right = Math.max(bounds.right, bottomRight.x);
    bounds.top = Math.min(bounds.top, topLeft.y);
    bounds.bottom = Math.max(bounds.bottom, bottomRight.y);
  }

  return {
    x: bounds.left - expandBy,
    y: bounds.top - expandBy,
    width: (bounds.right - bounds.left) + (2 * expandBy),
    height: (bounds.bottom - bounds.top) + (2 * expandBy),
  };
}

</script>

<template>
  <div
    :class="[
      'diff-item',
      'tw:flex tw:flex-col tw:px-2 tw:py-1',
      spacing === 'tight' ? 'tw:my-1 tw:mx-2' : 'tw:m-2',
      'tw:border-solid', 'tw:border', 'tw:rounded', 'tw:border-secondary', 'tw:border-s-8', statusDisplay[status].borderClass,
      visibility === VISIBILITY.NONE ? 'tw:cursor-not-allowed' : 'tw:cursor-pointer',
      visibility === VISIBILITY.NONE ? 'tw:bg-diff-inactive' : '',
    ]"
    :data-diff-item-key="[props.groupName, props.item.key].join('.')"
    :data-diff-item-status="status"
    @click="handleClick"
    @mouseenter="handleHoverStart"
    @mouseleave="handleHoverEnd"
  >
    <div
      v-if="pageName"
      class="tw:text-xs tw:text-text-light-3"
    >
      {{ pageName }}
    </div>
    <div class="tw:flex tw:gap-1 tw:items-center">
      <SvgIcon
        :name="statusIcon(status)"
        :class="statusClasses(status)"
      />
      <div
        :class="['tw:grow', {'tw:text-text-light-1': info.name === null}]"
      >
        {{ info.name ?? item.key }}
      </div>
      <SvgIcon
        v-if="visibility === VISIBILITY.NONE"
        name="octicon-eye-closed"
        class="tw:text-text-light-1"
        title="This change is not currently visible because it is on one or more hidden layers."
        data-tooltip-content="This change is not currently visible because it is on one or more hidden layers."
      />
      <SvgIcon
        v-if="visibility === VISIBILITY.PARTIAL"
        name="octicon-eye-closed"
        class="tw:text-text-light-3"
        title="This change is currently only partially visible due to hidden layers."
        data-tooltip-content="This change is currently only partially visible due to hidden layers."
      />
    </div>
    <div v-if="info.description">
      {{ info.description }}
    </div>
  </div>
</template>
